import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";

//importing custom modules
import { Button } from "../../components";
import Input from "../../components/UserInput/Input";
import { operatingTheatreSchema } from "../../yup";

// importing styleas
import style from "./operating-theatre.module.css";
import { colors } from "../../theme/colors";

// importing api
import { useAddOperatingTheatreMutation } from "../../services/api";
import { LabelBox } from "../../components";
import { RootState, useAppSelector } from "../../store";

type FormState = typeof operatingTheatreSchema.__outputType;

/**
 * Renders the add Operating Theatre form and handles submission.
 *
 * Returns:
 *   JSX.Element: Form UI for creating a new operating theatre.
 *
 * Notes:
 *   Requires authenticated user context for health_org_id; validation driven by operatingTheatreSchema.
 */
const AddOperatingTheatre = () => {
  const navigation = useNavigate();
  const [addOperatingTheatre] = useAddOperatingTheatreMutation();

  const { userDetails } = useAppSelector((state: RootState) => state.auth);

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<LabelMessageType | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormState>({
    resolver: yupResolver(operatingTheatreSchema),
    mode: "onChange",
  });

  /**
   * Submits a new operating theatre to the API.
   *
   * Args:
   *   values (FormState): Validated form data for the operating theatre.
   *
   * Returns:
   *   Promise<void>: Resolves after the create mutation completes.
   *
   * Raises:
   *   Displays an error message when the create mutation fails.
   */
  const onSubmit = async (values: FormState) => {
    try {
      setLoading(true);

      const payload: AddOperatingTheatrePayload = {
        name: values?.name,
        health_org_id: userDetails?.health_org_id ?? 0,
        room_no: values?.room_no ?? "",
        building: values?.building ?? "",
        floor: values?.floor ?? "",
      };

      const result = await addOperatingTheatre(payload).unwrap();
      setLoading(false);
      setMessage({
        type: "success",
        text: "Operating Theatre added successfully.",
      });
      setTimeout(() => {
        navigation(-1);
      }, 1500);
    } catch (error: unknown) {
      const parsedError =
        typeof error === "object" && error !== null
          ? (error as { message?: string; data?: { detail?: string } })
          : undefined;
      setMessage({
        type: "error",
        text:
          parsedError?.message ||
          parsedError?.data?.detail?.toString() ||
          "Please enter valid details",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Typography className={style.detailHeading}>
          Add New Operating Theatre
        </Typography>
        <Typography className={style.description}>
          Enter the below details to start adding a new operating theatre to
          Pegasus Cybersoft.
        </Typography>
        <Box className={style.flexTop}>
          <Box className={style.row}>
            <Input
              placeholder="Theatre Name"
              label="Theatre Name"
              required
              register={register}
              name="name"
              errors={errors}
            />
            <Input
              placeholder="Room Number"
              label="Room Number"
              register={register}
              name="room_no"
              errors={errors}
            />
          </Box>
          <Box className={style.row}>
            <Input
              placeholder="Floor"
              label="Floor"
              register={register}
              name="floor"
              errors={errors}
            />
            <Input
              placeholder="Building"
              label="Building"
              register={register}
              name="building"
              errors={errors}
            />
          </Box>

          <Box className={style.row}></Box>
          {message && (
            <Box mt={2}>
              <LabelBox
                boxProps={{ height: 50, display: "flex", alignItems: "center" }}
                data={message}
                onClose={() => setMessage(null)}
              />
            </Box>
          )}
          <Box className={style.buttons}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label="Cancel"
            />
            <Button
              loading={loading}
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              type="submit"
              label="Save"
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AddOperatingTheatre;
